import React from "react";
import { useUserControls } from "../../context/UserControlsProvider";
import { usePrimaryFetchedData } from "../../context/PrimaryFetchedDataContext";
import {
  SwipeableDrawer,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Typography,
  Divider,
} from "@mui/material";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import { formatStatusLabel } from "../../utils/status";
import { isVerboseLoggingEnabled } from "../../utils/debug";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CircularProgress from "@mui/material/CircularProgress";

type MenuItem = {
  key?: string | number;
  label: React.ReactNode;
  onClick?: () => void;
};

export function useAppBarMenus(): {
  menuItems: MenuItem[];
  drawers: React.ReactNode;
  shouldPromptSelection: boolean;
} {
  const {
    possibleShipmentIds,
    setSelectedShipmentIndex,
    possibleLocations,
    setUnloadLocation,
    selectedShipment,
    unloadLocation,
    actionStatus,
    setActionStatus,
  } = useUserControls();
  const { shipments } = usePrimaryFetchedData();
  const verbose = isVerboseLoggingEnabled();

  const [shipmentDrawerOpen, setShipmentDrawerOpen] = React.useState(false);
  const [locationDrawerOpen, setLocationDrawerOpen] = React.useState(false);
  const [statusDrawerOpen, setStatusDrawerOpen] = React.useState(false);
  const [pendingShipmentId, setPendingShipmentId] = React.useState<string | null>(null);

  const handleOpenShipmentMenu = () => {
    if (verbose) {
      console.log("[useAppBarMenus] Opening shipment drawer from menu");
    }
    setShipmentDrawerOpen(true);
  };
  const handleOpenLocationMenu = () => {
    if (verbose) {
      console.log("[useAppBarMenus] Opening location drawer from menu");
    }
    setLocationDrawerOpen(true);
  };
  const handleOpenStatusMenu = () => {
    if (verbose) {
      console.log("[useAppBarMenus] Opening status drawer from menu");
    }
    setStatusDrawerOpen(true);
  };
  const markShipmentSelectionStart = React.useCallback(
    (shipmentId: string | undefined, timerLabel: string | null) => {
      if (typeof window === "undefined" || typeof performance === "undefined") {
        return;
      }
      try {
        if (timerLabel && typeof console !== "undefined" && console.time) {
          console.time(timerLabel);
        }
        (window as any).__cabinetSelectionDiag = {
          shipmentId,
          startedAt: performance.now(),
          loggedPhases: {},
          timerLabel,
        };
        console.info("[SelectionDiagnostics] selection started", {
          shipmentId,
          startedAt: (window as any).__cabinetSelectionDiag.startedAt,
        });
      } catch (error) {
        console.warn("[SelectionDiagnostics] failed to mark start", error);
      }
    },
    []
  );

  const handleSelectShipment = (shipmentIndex: number) => {
    const shipmentId = possibleShipmentIds[shipmentIndex];
    const timerLabel = shipmentId
      ? `[SelectShipment] ${shipmentId}`
      : `[SelectShipmentIndex] ${shipmentIndex}`;
    setPendingShipmentId(shipmentId ?? null);
    if (verbose) {
      console.log("[useAppBarMenus] Selecting shipment", {
        shipmentIndex,
        shipmentId,
      });
    }
    markShipmentSelectionStart(shipmentId, timerLabel);
    setShipmentDrawerOpen(false);

    const runSelection = () => {
      if (timerLabel && typeof console !== "undefined" && typeof console.timeLog === "function") {
        console.timeLog(timerLabel, "before-selection-state");
      }
      // Shipment should never auto-select a location; clear any prior location
      setUnloadLocation("");
      setSelectedShipmentIndex(shipmentIndex);
    };

    if (typeof window !== "undefined") {
      if (typeof window.requestAnimationFrame === "function") {
        window.requestAnimationFrame(() => {
          runSelection();
        });
      } else {
        window.setTimeout(runSelection, 0);
      }
    } else {
      runSelection();
    }
  };
  const handleSelectLocation = (location: string) => {
    if (verbose) {
      console.log("[useAppBarMenus] Selecting location", { location });
    }
    setUnloadLocation(location);
    setLocationDrawerOpen(false);
  };
  const handleSelectStatus = (newStatus: string) => {
    if (verbose) {
      console.log("[useAppBarMenus] Selecting actionStatus", { newStatus });
    }
    setActionStatus(newStatus);
    setStatusDrawerOpen(false);
  };

  const hasShipmentSelection = selectedShipment !== "";
  const hasLocationSelection = unloadLocation !== "";
  if (verbose) {
    console.log('[useAppBarMenus] state snapshot', {
      selectedShipment,
      unloadLocation,
      possibleShipmentIdsCount: possibleShipmentIds.length,
      possibleLocationsCount: possibleLocations.length,
      hasShipmentSelection,
      hasLocationSelection,
      actionStatus,
    });
  }

  const shipmentLabel = hasShipmentSelection ? "Change Shipment" : "Select Shipment";
  const locationLabel = hasLocationSelection ? "Change Location" : "Select Location";

  const needsShipmentSelection = !hasShipmentSelection;
  const needsLocationSelection = !hasLocationSelection;

  React.useEffect(() => {
    if (verbose) {
      console.log("[useAppBarMenus] Selection status", {
        selectedShipment,
        unloadLocation,
        possibleShipmentIdsCount: possibleShipmentIds.length,
        possibleLocationsCount: possibleLocations.length,
        needsShipmentSelection,
        needsLocationSelection,
      });
    }
  }, [
    selectedShipment,
    unloadLocation,
    possibleShipmentIds.length,
    possibleLocations.length,
    needsShipmentSelection,
    needsLocationSelection,
    verbose,
  ]);

  const lastAutoOpenedTargetRef = React.useRef<"shipment" | "location" | null>(
    null
  );
  // Track a stable key so dev StrictMode (double effect) and tab remounts don’t double-open
  const lastAutoOpenKeyRef = React.useRef<string | null>(null);
  const waitForInteractivePage = React.useCallback(
    (timeoutMs: number = 500): Promise<void> => {
      if (typeof window === "undefined" || typeof document === "undefined") {
        return Promise.resolve();
      }
      const startedAt = performance.now?.() ?? Date.now();
      const start = performance.now?.() ?? Date.now();
      return new Promise((resolve) => {
        const check = () => {
          const activePage = document.querySelector<HTMLElement>(
            "ion-router-outlet > .ion-page:not(.ion-page-hidden)"
          );
          if (!activePage) {
            try {
              console.info("[useAppBarMenus] waitForInteractivePage resolved (no active page)", {
                elapsedMs: (performance.now?.() ?? Date.now()) - startedAt,
              });
            } catch (_) {}
            resolve();
            return;
          }
          const computed = window.getComputedStyle(activePage);
          if (computed.pointerEvents !== "none") {
            try {
              console.info("[useAppBarMenus] waitForInteractivePage resolved", {
                elapsedMs: (performance.now?.() ?? Date.now()) - startedAt,
                pointerEvents: computed.pointerEvents,
                page: activePage.className,
              });
            } catch (_) {}
            resolve();
            return;
          }
          const now = performance.now?.() ?? Date.now();
          if (now - start >= timeoutMs) {
            try {
              console.warn("[useAppBarMenus] waitForInteractivePage timed out", {
                timeoutMs,
                lastPointerEvents: computed.pointerEvents,
              });
            } catch (_) {}
            resolve();
            return;
          }
          requestAnimationFrame(check);
        };
        check();
      });
    },
    []
  );

  const autoOpenDrawer = React.useCallback(
    (target: "shipment" | "location") => {
      void waitForInteractivePage().then(() => {
        if (target === "shipment") {
          setShipmentDrawerOpen(true);
          setLocationDrawerOpen(false);
        } else {
          setLocationDrawerOpen(true);
          setShipmentDrawerOpen(false);
        }
      });
    },
    [waitForInteractivePage, setShipmentDrawerOpen, setLocationDrawerOpen]
  );

  React.useEffect(() => {
    let nextTarget: "shipment" | "location" | null = null;
    if (needsShipmentSelection && possibleShipmentIds.length > 0) {
      nextTarget = "shipment";
    } else if (needsLocationSelection && possibleLocations.length > 0) {
      nextTarget = "location";
    }

    const autoOpenKey = nextTarget
      ? `${nextTarget}|shipment:${selectedShipment || "<none>"}|loc:${unloadLocation || "<none>"}|shipCount:${possibleShipmentIds.length}|locCount:${possibleLocations.length}`
      : null;

    if (
      nextTarget &&
      (lastAutoOpenedTargetRef.current !== nextTarget ||
        lastAutoOpenKeyRef.current !== autoOpenKey)
    ) {
      if (nextTarget === "shipment") {
        if (verbose) {
          console.log(
            "[useAppBarMenus] Auto-opening shipment drawer due to missing selection",
            { autoOpenKey }
          );
        }
        autoOpenDrawer("shipment");
      } else {
        if (verbose) {
          console.log(
            "[useAppBarMenus] Auto-opening location drawer due to missing selection",
            { autoOpenKey }
          );
        }
        autoOpenDrawer("location");
      }
      lastAutoOpenedTargetRef.current = nextTarget;
      lastAutoOpenKeyRef.current = autoOpenKey;
    } else if (!nextTarget) {
      if (verbose && lastAutoOpenedTargetRef.current !== null) {
        console.log("[useAppBarMenus] All selections made, resetting auto-open state");
      }
      lastAutoOpenedTargetRef.current = null;
      lastAutoOpenKeyRef.current = null;
    } else {
      if (verbose) {
        console.log(
          "[useAppBarMenus] Auto-open skipped (target unchanged or options unavailable)",
          {
            nextTarget,
            lastAutoOpenedTarget: lastAutoOpenedTargetRef.current,
            lastAutoOpenKey: lastAutoOpenKeyRef.current,
            currentAutoOpenKey: autoOpenKey,
            possibleShipmentIdsCount: possibleShipmentIds.length,
            possibleLocationsCount: possibleLocations.length,
          }
        );
      }
    }
  }, [
    needsLocationSelection,
    needsShipmentSelection,
    possibleLocations.length,
    possibleShipmentIds.length,
    selectedShipment,
    unloadLocation,
    autoOpenDrawer,
    verbose,
  ]);

  React.useEffect(() => {
    if (!selectedShipment) {
      return;
    }
    setPendingShipmentId((prev) =>
      prev && prev === selectedShipment ? null : prev
    );
  }, [selectedShipment]);

  React.useEffect(() => {
    if (!shipmentDrawerOpen) {
      setPendingShipmentId(null);
    }
  }, [shipmentDrawerOpen]);

  const menuItems: MenuItem[] = [
    { label: shipmentLabel, onClick: handleOpenShipmentMenu },
    { label: locationLabel, onClick: handleOpenLocationMenu },
    { label: "Change Status", onClick: handleOpenStatusMenu },
  ];
  if (verbose) {
    console.log('[useAppBarMenus] built menuItems', menuItems.map(m => String(m.label)));
  }

  const drawers = (
    <>
      <SwipeableDrawer
        anchor="bottom"
        open={shipmentDrawerOpen}
        onClose={() => setShipmentDrawerOpen(false)}
        onOpen={() => setShipmentDrawerOpen(true)}
        disableSwipeToOpen
      >
        <div style={{ padding: 8, maxHeight: "50vh", overflow: "auto" }}>
          <Typography variant="subtitle1" sx={{ px: 1, py: 1 }}>
            {shipmentLabel}
          </Typography>
          <Divider />
          <List sx={{ px: 0.5, py: 0.5 }}>
            {possibleShipmentIds.map((shipmentId, index) => {
              const shipment = shipments.find((s) => s.id === shipmentId);
              let dateLabel = "";
              try {
                const ts: any = shipment?.shipmentDate as any;
                const jsDate: Date | null = ts && typeof ts.toDate === "function" ? ts.toDate() : ts instanceof Date ? ts : null;
                if (jsDate) {
                  dateLabel = jsDate.toLocaleDateString(undefined, {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  });
                }
              } catch (_) {
                // ignore formatting errors; leave dateLabel empty
              }
              const statusRaw = (shipment as any)?.status as string | undefined;
              const statusLabel = formatStatusLabel(statusRaw);
              const isSelected = selectedShipment === shipmentId;
              const isPending =
                pendingShipmentId === shipmentId && !isSelected;
              return (
                <ListItem
                  key={shipmentId}
                  disablePadding
                  sx={{
                    mb: 0.5,
                    borderRadius: 1,
                  }}
                >
                  <ListItemButton
                    onClick={() => handleSelectShipment(index)}
                    selected={isSelected}
                    sx={{
                      borderRadius: 1,
                      "&.Mui-selected": {
                        bgcolor: "action.selected",
                      },
                      "&.MuiButtonBase-root": {
                        alignItems: "center",
                      },
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: "flex", alignItems: "baseline" }}>
                          <Typography component="span">{shipmentId}</Typography>
                          {dateLabel ? (
                            <Typography
                              component="span"
                              variant="caption"
                              sx={{ ml: 1, color: "text.secondary" }}
                            >
                              {dateLabel}
                            </Typography>
                          ) : null}
                        </Box>
                      }
                    />
                    {statusLabel ? (
                      <Chip size="small" label={statusLabel} color="info" sx={{ ml: 1 }} />
                    ) : null}
                    {isPending ? (
                      <CircularProgress size={18} sx={{ ml: 1 }} />
                    ) : isSelected ? (
                      <CheckCircleIcon
                        color="success"
                        sx={{ ml: 1, fontSize: 20 }}
                        aria-hidden
                      />
                    ) : null}
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </div>
      </SwipeableDrawer>

      <SwipeableDrawer
        anchor="bottom"
        open={locationDrawerOpen}
        onClose={() => setLocationDrawerOpen(false)}
        onOpen={() => setLocationDrawerOpen(true)}
        disableSwipeToOpen
      >
        <div style={{ padding: 8, maxHeight: "50vh", overflow: "auto" }}>
          <Typography variant="subtitle1" sx={{ px: 1, py: 1 }}>
            {locationLabel}
          </Typography>
          <Divider />
          <List>
            {possibleLocations.map((location) => (
              <ListItem key={location}>
                <ListItemButton onClick={() => handleSelectLocation(location)}>
                  {location}
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </div>
      </SwipeableDrawer>

      <SwipeableDrawer
        anchor="bottom"
        open={statusDrawerOpen}
        onClose={() => setStatusDrawerOpen(false)}
        onOpen={() => setStatusDrawerOpen(true)}
        disableSwipeToOpen
      >
        <div style={{ padding: 8, maxHeight: "50vh", overflow: "auto" }}>
          <Typography variant="subtitle1" sx={{ px: 1, py: 1 }}>
            Change Status
          </Typography>
          <Divider />
          <List>
            {[
              "pending",
              "loading",
              "unloading",
              "damaged",
              "missing",
              "delayed",
            ].map((st) => (
              <ListItem key={st}>
                <ListItemButton onClick={() => handleSelectStatus(st)}>
                  {formatStatusLabel(st)}
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </div>
      </SwipeableDrawer>
    </>
  );

  return {
    menuItems,
    drawers,
    shouldPromptSelection: needsShipmentSelection || needsLocationSelection,
  };
}

export default useAppBarMenus;
